import { Box, Typography } from '@mui/material'
import React from 'react'
import { i18n } from '../../index/index'
import { MonthlyActualValue, MonthlyTargetValue } from './types'
import { useValueFormat } from './valueFormat'
import { deriveMonthlyRows } from './deriveMonthlyRows'
import { MonthlyTargetListItem } from './MonthlyTargetListItem'

export interface MonthlyTargetsListProps {
  months: number[]
  targetYear: number
  heading: string
  monthlyTargets: MonthlyTargetValue[]
  lastYearActuals?: MonthlyActualValue[]
  isEditing: boolean
  unallocated: number
  focusedMonth: number | null
  onFocusMonth: (month: number | null) => void
  changeMonthlyTarget: (month: number, value: number) => void
  fillRemainderIntoMonth: (month: number) => void
}

/**
 * Mobile monthly editor (Figma): months rendered as a stacked list (name + last-year
 * sublabel + right-aligned input) instead of a table. Shares deriveMonthlyRows with the
 * desktop table so the two layouts stay in sync.
 */
export function MonthlyTargetsList({
  months,
  targetYear,
  heading,
  monthlyTargets,
  lastYearActuals,
  isEditing,
  unallocated,
  focusedMonth,
  onFocusMonth,
  changeMonthlyTarget,
  fillRemainderIntoMonth,
}: MonthlyTargetsListProps) {
  const { format } = useValueFormat()
  const lastYear = targetYear - 1
  const { rows, hasLastYear, targetSum } = deriveMonthlyRows(months, monthlyTargets, lastYearActuals, targetYear)

  return (
    <>
      <Typography gutterBottom>{heading}</Typography>
      {rows.map((row) => (
        <MonthlyTargetListItem
          key={row.month}
          month={row.month}
          monthlyTarget={row.monthlyTarget}
          lastYearOutcome={row.lastYearOutcome}
          lastYear={lastYear}
          hasLastYear={hasLastYear}
          isCurrentMonth={row.isCurrentMonth}
          isEditing={isEditing && !row.isLocked}
          unallocated={unallocated}
          isFocused={focusedMonth === row.month}
          onFocusMonth={onFocusMonth}
          changeMonthlyTarget={changeMonthlyTarget}
          fillRemainderIntoMonth={fillRemainderIntoMonth}
        />
      ))}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1, minHeight: 40 }}>
        <Typography variant='body1' fontWeight={600}>
          {i18n.t('admin.monthlyTargets.totalRow')}
        </Typography>
        <Typography variant='body1' fontWeight={600}>
          {format(targetSum)}
        </Typography>
      </Box>
    </>
  )
}
